/** External */
import React, { useState } from "react";
import { connect } from "react-redux";
import _ from "lodash";

/** Design System **/
import { Modal, Button, Alert } from "@toddle-edu/ds-web";

/** Internal */
import { useI18n } from "Hooks";
import { compose, graphql } from "lib/apolloClient";
import { FormTextInput } from "UIComponents/InputTextField";
import withLoader from "UIComponents/GqlWithLoader";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { getInternalToolGenericDataQuery } from "InternalTools/modules/InternalToolsQuery";
import { updateAddOnStatus } from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnHelpers";
import {
  TODDLE_ANALYTICS,
  TODDLE_ANALYTICS_MODULES,
} from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnContants";
import {
  getMutationInput,
  getAllModulesLastSyncQueryVariables,
  isTimezoneConfigured,
  getConfigurationQueryFilters,
} from "AddOnManager/routes/OrgAddOn/components/ToddleAnalytics/modules/Utils";
import { getLastSyncTimeQueryEnhancer } from "ToddleAnalytics/modules/ToddleAnalyticsQueryEnhancers";
import { getOrganizationGlobalConstantsQuery } from "modules/CommonQuery";
import { getConfigurationValuesQuery } from "Tooling/modules/ToolingQueries";

/** Relative */
import { getOrganizationCurriculumProgramsTypesQuery } from "./modules/ToddleAnalyticsQueries";
import CurriculumProgramAccordion from "./components/CurriculumProgramAccordion";
import classes from "./ToddleAnalytics.scss";

const DebouncedInput = ({ organizationId, disableOrgIdInput }) => {
  const { t } = useI18n();

  return (
    <FormTextInput
      size="large"
      label={t("common:org_id")}
      value={organizationId}
      isRequired={true}
      disabled={disableOrgIdInput}
    />
  );
};

export const ToddleAnalytics = ({
  orgId,
  closeModal,
  curriculumProgramsOfOrg,
  analyticsModulesStatus,
  updateAddOnStatus,
  interalToolGenericDataQueryVariables,
  organizationDetails,
  onSuccessAddOnConfigChange = null,
}) => {
  const { t } = useI18n();

  const [fullScreenLoader, setFullScreenLoader] = useState(false);
  const [statusMapping, setStatusMapping] = useState(analyticsModulesStatus);

  const onClickSave = async () => {
    const mutationInput = getMutationInput({
      previousPermissions: analyticsModulesStatus,
      currentPermissions: statusMapping,
      curriculumPrograms: curriculumProgramsOfOrg,
    });
    setFullScreenLoader(true);
    await updateAddOnStatus({
      orgId,
      addOnType: TODDLE_ANALYTICS,
      newStatus: mutationInput,
      queryVariables: interalToolGenericDataQueryVariables,
    });
    if (!_.isNil(onSuccessAddOnConfigChange)) onSuccessAddOnConfigChange();
    closeModal();
    setFullScreenLoader(false);
  };

  const showTimezoneAlert = !isTimezoneConfigured({ organizationDetails });
  const modalHeight = showTimezoneAlert ? "400px" : "700px";

  return (
    <>
      <Modal width="800px" height={modalHeight} isOpen onClose={closeModal}>
        <Modal.Header
          title={t("toddleAnalytics:enable_disable_toddle_insights_features")}
          isClosable
        />
        <Modal.Body isScrollable>
          <div className={classes.container}>
            <DebouncedInput organizationId={orgId} disableOrgIdInput={true} />
            {showTimezoneAlert ? (
              <Alert
                type="error"
                title={t("toddleAnalytics:timezone_not_configured")}
                message={t("toddleAnalytics:no_timzone_alert")}
                showIcon
              />
            ) : (
              <>
                <Alert
                  type="info"
                  message={t("toddleAnalytics:add_on_manager_insights_alert")}
                  showIcon
                />
                <h6>
                  {t(
                    "toddleAnalytics:enable_disable_insights_modules_for_each_curriculum"
                  )}
                </h6>
                <div className={classes.curriculumProgramsContainer}>
                  {_.map(curriculumProgramsOfOrg, curriculumProgram => {
                    const handleToggleSwitch = ({ moduleName }) => {
                      setStatusMapping(prev => {
                        const curriculumProgramId = _.get(
                          curriculumProgram,
                          "id"
                        );
                        const mappingId = `${curriculumProgramId}-${moduleName}`;
                        const prevStatus = _.get(prev, mappingId);

                        return {
                          ...prev,
                          [mappingId]: !prevStatus,
                        };
                      });
                    };

                    return (
                      <CurriculumProgramAccordion
                        orgId={orgId}
                        curriculumProgram={curriculumProgram}
                        moduleStatusOriginalMapping={analyticsModulesStatus}
                        statusMapping={statusMapping}
                        handleToggleSwitch={handleToggleSwitch}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={classes.footer}>
            <div className={classes.rightFooterContainer}>
              <Button
                _legacy_variant="outlined-subtle"
                variant="neutral"
                type="outlined"
                size="large"
                onClick={closeModal}
              >
                {t("common:close")}
              </Button>
              {!showTimezoneAlert ? (
                <Button
                  variant="progressive"
                  size="large"
                  onClick={onClickSave}
                >
                  {t("common:save")}
                </Button>
              ) : null}
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      {fullScreenLoader ? <FullScreenLoader /> : null}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { orgId } = ownProps;

  const interalToolGenericDataQueryVariables = {
    input: {
      apiType: "ADD_ON_MANAGER",
      params: {
        organizationId: orgId,
        addOnType: TODDLE_ANALYTICS,
      },
    },
  };

  const allModulesLastSyncQueryVariables = getAllModulesLastSyncQueryVariables({
    modules: TODDLE_ANALYTICS_MODULES,
    orgId,
  });

  const configQueryFilters = getConfigurationQueryFilters({ orgId });

  return {
    isData: true,
    isLoading: false,
    organizationId: orgId,
    configQueryFilters,
    interalToolGenericDataQueryVariables,
    ...allModulesLastSyncQueryVariables,
  };
};

const mapActionCreators = { updateAddOnStatus };

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getConfigurationValuesQuery, {
    name: "getConfigurationValuesQuery",
    options: ({ configQueryFilters: filters }) => ({
      fetchPolicy: "cache-and-network",
      variables: { filters },
    }),
    props({ getConfigurationValuesQuery, ownProps: { isData, isLoading } }) {
      const data = _.get(getConfigurationValuesQuery, "platform");
      const networkStatus = _.get(getConfigurationValuesQuery, "networkStatus");
      return {
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getOrganizationGlobalConstantsQuery, {
    name: "getOrganizationGlobalConstants",
    skip: ({ organizationId }) => !organizationId,
    options: ({ organizationId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        filter: {
          names: ["TIMEZONE"],
        },
      },
    }),
    props({ getOrganizationGlobalConstants, ownProps: { isData, isLoading } }) {
      const data = _.get(getOrganizationGlobalConstants, "node", {});
      const networkStatus = _.get(
        getOrganizationGlobalConstants,
        "networkStatus"
      );
      return {
        organizationDetails: _.get(data, "globalConstants"),
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getOrganizationCurriculumProgramsTypesQuery, {
    name: "getOrganizationCurriculumProgramsTypes",
    options: ({ organizationId, curriculumProgramIds = [] }) => {
      return {
        variables: {
          organizationId,
          curriculumProgramIds,
        },
        fetchPolicy: "cache-and-network",
      };
    },
    props: ({
      getOrganizationCurriculumProgramsTypes,
      ownProps: { isData, isLoading },
    }) => {
      const data = getOrganizationCurriculumProgramsTypes || {};

      const curriculumProgramsOfOrg = _.get(
        data,
        "node.curriculumPrograms",
        []
      );

      return {
        curriculumProgramsOfOrg,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            [1, 2],
            getOrganizationCurriculumProgramsTypes["networkStatus"]
          ) || isLoading,
      };
    },
  }),
  graphql(getInternalToolGenericDataQuery, {
    name: "getInternalToolGenericData",
    options: ({ interalToolGenericDataQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: interalToolGenericDataQueryVariables,
    }),
    props: ({
      getInternalToolGenericData,
      ownProps: { isData, isLoading },
    }) => {
      const queryData = _.get(
        getInternalToolGenericData,
        "platform.internalToolsGenericQuery",
        {}
      );

      const curriculumPrograms = _.get(
        queryData,
        "data.curriculumPrograms",
        []
      );

      const analyticsModulesStatus = _.reduce(
        curriculumPrograms,
        (prev, curriculumProgram) => {
          const curriculumProgramId = _.get(curriculumProgram, "id");
          const status = _.get(curriculumProgram, "status");
          const statusMapping = {};
          _.forEach(TODDLE_ANALYTICS_MODULES, module => {
            const moduleName = _.get(module, "id");
            statusMapping[`${curriculumProgramId}-${moduleName}`] =
              status[`${moduleName}_ANALYTICS`];
          });
          return {
            ...prev,
            ...statusMapping,
          };
        },
        {}
      );

      return {
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          _.includes([1, 2], getInternalToolGenericData["networkStatus"]) ||
          isLoading,
        analyticsModulesStatus,
      };
    },
  }),
  getLastSyncTimeQueryEnhancer({
    queryVariablesKey: "engagementLastSyncQueryVariables",
  }),
  getLastSyncTimeQueryEnhancer({
    queryVariablesKey: "performanceLastSyncQueryVariables",
  }),
  getLastSyncTimeQueryEnhancer({
    queryVariablesKey: "behaviourLastSyncQueryVariables",
  }),
  getLastSyncTimeQueryEnhancer({
    queryVariablesKey: "usageLastSyncQueryVariables",
  }),
  getLastSyncTimeQueryEnhancer({
    queryVariablesKey: "attendanceLastSyncQueryVariables",
  }),
  withLoader
)(ToddleAnalytics);
